import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { 
  Check, X, Crown, Zap, Users, Calendar, Download, Clock, 
  Shield, Star, ArrowRight, Loader2, CheckCircle2, AlertCircle, Ticket, Tag, 
  Key, Palette, Code, Copy, Trash2, Plus, Eye, EyeOff, CreditCard
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { apiKeysAPI, whiteLabelAPI, cardBillingAPI } from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [processing, setProcessing] = useState(false);
  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState(null); // null | { valid, message, ... }
  const [couponLoading, setCouponLoading] = useState(false);
  const [redeemingCoupon, setRedeemingCoupon] = useState(false);
  
  // Enterprise state
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState(['read']);
  const [createdKey, setCreatedKey] = useState(null);
  const [showCreatedKey, setShowCreatedKey] = useState(false);
  const [whiteLabel, setWhiteLabel] = useState({ brand_name: '', primary_color: '#0067B9', accent_color: '#007A3D', logo_url: '', hide_powered_by: false });
  const [savingBrand, setSavingBrand] = useState(false);

  // Card payment state
  const [billingConfig, setBillingConfig] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card'); // card | paypal
  const [cardProcessing, setCardProcessing] = useState(false);
  const [cardSubscription, setCardSubscription] = useState(null);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const [subRes, configRes] = await Promise.all([
        axios.get(`${API}/subscription/status`, { withCredentials: true }),
        cardBillingAPI.getConfig().catch(() => ({ data: {} })),
      ]);
      const response = subRes;
      setSubscription(response.data.subscription);
      setUsage(response.data.usage);
      setPricing(response.data.pricing);
      setPaypalClientId(response.data.paypal_client_id);
      setBillingConfig(configRes.data);

      // Set default payment method based on config
      if (configRes.data?.card_enabled) {
        setPaymentMethod('card');
      } else if (configRes.data?.paypal_enabled !== false) {
        setPaymentMethod('paypal');
      }
      
      // Load PayPal SDK if configured
      const paypalOn = configRes.data?.paypal_enabled !== false;
      if (paypalOn && response.data.paypal_client_id && !window.paypal) {
        loadPayPalScript(response.data.paypal_client_id);
      } else if (window.paypal) {
        setPaypalReady(true);
      }
      
      // If enterprise tier, fetch API keys and white-label settings
      if (response.data.subscription?.tier === 'enterprise') {
        fetchEnterpriseData();
      }

      // Fetch card subscription info
      try {
        const cardSubRes = await cardBillingAPI.getCardSubscription();
        setCardSubscription(cardSubRes.data);
      } catch {}
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast({ title: 'Error loading subscription data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchEnterpriseData = async () => {
    try {
      const [keysRes, brandRes] = await Promise.all([
        apiKeysAPI.getAll().catch(() => ({ data: [] })),
        whiteLabelAPI.get().catch(() => ({ data: { settings: {} } })),
      ]);
      setApiKeys(keysRes.data || []);
      if (brandRes.data?.settings) {
        setWhiteLabel(prev => ({ ...prev, ...brandRes.data.settings }));
      }
    } catch (e) { console.error(e); }
  };

  const loadPayPalScript = (clientId) => {
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
    script.async = true;
    script.onload = () => setPaypalReady(true);
    document.body.appendChild(script);
  };

  const handlePayPalPayment = async (planType = 'yearly') => {
    if (!paypalReady || !window.paypal) {
      toast({ title: 'PayPal is not ready. Please try again.', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    const amount = planType === 'enterprise' ? 99 : 29;
    const tierLabel = planType === 'enterprise' ? 'Enterprise' : 'Pro';

    try {
      const orderResponse = await axios.post(
        `${API}/subscription/create-paypal-order?plan=${planType}`,
        {},
        { withCredentials: true }
      );
      
      const orderId = orderResponse.data.order_id;
      
      // Clear any previous PayPal button
      const container = document.getElementById('paypal-button-container');
      if (container) container.innerHTML = '';
      
      window.paypal.Buttons({
        createOrder: () => orderId,
        onApprove: async (data) => {
          try {
            const captureResponse = await axios.post(
              `${API}/subscription/capture-paypal-order/${data.orderID}`,
              {},
              { withCredentials: true }
            );
            
            if (captureResponse.data.status === 'success') {
              toast({ title: `Payment successful! ${tierLabel} subscription activated for 1 year.` });
              fetchSubscriptionStatus();
            }
          } catch (error) {
            toast({ title: 'Failed to process payment', variant: 'destructive' });
          }
        },
        onCancel: () => {
          toast({ title: 'Payment cancelled' });
        },
        onError: (err) => {
          console.error('PayPal error:', err);
          toast({ title: 'Payment error occurred', variant: 'destructive' });
        }
      }).render('#paypal-button-container');
      
    } catch (error) {
      toast({ title: 'Failed to create payment', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  // Handle return from PayPal (for redirect flow)
  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'true') {
      toast({ title: 'Payment successful! Your Pro subscription is now active for 1 year.' });
      fetchSubscriptionStatus();
      // Clean URL
      window.history.replaceState({}, document.title, '/subscription');
    }
  }, [searchParams]);

  const handleContactForPayment = () => {
    window.location.href = 'mailto:info@attendancetracker.ws?subject=Pro%20Subscription%20Request&body=Hi,%20I%20would%20like%20to%20upgrade%20to%20a%20Pro%20subscription.';
  };

  const handleCardCheckout = async (productId) => {
    setCardProcessing(true);
    try {
      const res = await cardBillingAPI.checkout({
        product_id: productId,
        coupon_code: couponStatus?.valid ? couponCode : null,
      });

      if (res.data.status === 'free_upgrade') {
        toast({ title: 'Coupon applied! Subscription activated.' });
        fetchSubscriptionStatus();
        return;
      }

      // Submit hidden form to hosted page
      const { hosted_url, fields } = res.data;
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = hosted_url;
      Object.entries(fields).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = v;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Checkout failed', variant: 'destructive' });
    } finally {
      setCardProcessing(false);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponStatus(null);
    try {
      const response = await axios.get(`${API}/subscription/validate-coupon/${couponCode.trim()}`, { withCredentials: true });
      setCouponStatus(response.data);
    } catch (error) {
      setCouponStatus({ valid: false, message: error.response?.data?.detail || 'Error validating coupon' });
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) return;
    setRedeemingCoupon(true);
    try {
      const response = await axios.post(`${API}/subscription/redeem-coupon`, { code: couponCode.trim() }, { withCredentials: true });
      if (response.data.status === 'success') {
        toast({ title: response.data.message });
        setCouponCode('');
        setCouponStatus(null);
        fetchSubscriptionStatus();
      } else if (response.data.status === 'discount') {
        setCouponStatus({ valid: true, ...response.data });
        toast({ title: response.data.message });
      }
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error redeeming coupon', variant: 'destructive' });
    } finally {
      setRedeemingCoupon(false);
    }
  };

  const isPro = subscription?.tier === 'pro';
  const isEnterprise = subscription?.tier === 'enterprise';
  const isPaid = isPro || isEnterprise;

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const res = await apiKeysAPI.create({ name: newKeyName.trim(), scopes: newKeyScopes });
      setCreatedKey(res.data);
      setShowCreatedKey(true);
      setNewKeyName('');
      fetchEnterpriseData();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Failed to create API key', variant: 'destructive' });
    }
  };

  const handleRevokeKey = async (id) => {
    try {
      await apiKeysAPI.revoke(id);
      toast({ title: 'API key revoked' });
      fetchEnterpriseData();
    } catch (err) {
      toast({ title: 'Failed to revoke key', variant: 'destructive' });
    }
  };

  const handleSaveBranding = async () => {
    setSavingBrand(true);
    try {
      await whiteLabelAPI.update(whiteLabel);
      toast({ title: 'Branding updated!' });
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Failed to save branding', variant: 'destructive' });
    } finally {
      setSavingBrand(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-800">Subscription</h1>
        <p className="text-gray-600 mt-2">Manage your plan and usage</p>
      </div>

      {/* Current Plan Status */}
      <Card className={isEnterprise ? 'border-2 border-indigo-500 bg-indigo-50' : isPro ? 'border-2 border-purple-500 bg-purple-50' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {isEnterprise ? (
                <Key className="h-8 w-8 text-indigo-600" />
              ) : isPaid ? (
                <Crown className="h-8 w-8 text-purple-600" />
              ) : (
                <Shield className="h-8 w-8 text-gray-400" />
              )}
              <div>
                <CardTitle className="flex items-center space-x-2">
                  <span>Current Plan: {isEnterprise ? 'Enterprise' : isPro ? 'Pro' : 'Free'}</span>
                  {isPaid && <Badge className={isEnterprise ? 'bg-indigo-600' : 'bg-purple-600'}>Active</Badge>}
                </CardTitle>
                {isPaid && subscription?.current_period_end && (
                  <CardDescription>
                    {subscription.cancel_at_period_end 
                      ? `Expires on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                      : `Renews on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                    }
                  </CardDescription>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        
        {usage && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Programs Usage */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Programs</span>
                  <span className="font-medium">
                    {usage.programs_count} / {usage.programs_limit < 0 ? '∞' : usage.programs_limit}
                  </span>
                </div>
                <Progress 
                  value={usage.programs_limit < 0 ? 10 : (usage.programs_count / usage.programs_limit) * 100} 
                  className="h-2"
                />
              </div>
              
              {/* Athletes Usage */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Athletes</span>
                  <span className="font-medium">
                    {usage.athletes_count} / {usage.athletes_limit < 0 ? '∞' : usage.athletes_limit}
                  </span>
                </div>
                <Progress 
                  value={usage.athletes_limit < 0 ? 10 : (usage.athletes_count / usage.athletes_limit) * 100} 
                  className="h-2"
                />
              </div>
              
              {/* Team Members Usage */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Team Members</span>
                  <span className="font-medium">
                    {usage.team_members_count} / {usage.team_members_limit < 0 ? '∞' : usage.team_members_limit}
                  </span>
                </div>
                <Progress 
                  value={usage.team_members_limit < 0 ? 10 : (usage.team_members_count / usage.team_members_limit) * 100} 
                  className="h-2"
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Upgrade Section (show if not on Enterprise) */}
      {!isEnterprise && (
        <>
          {/* Plan Cards */}
          <div className={`grid grid-cols-1 ${isPaid ? 'max-w-md mx-auto' : 'md:grid-cols-2 max-w-3xl mx-auto'} gap-6`}>
            {/* Pro Plan - only show if not already Pro */}
            {!isPaid && (
              <Card className="border-2 border-blue-500 shadow-lg" data-testid="pro-plan-card">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center space-x-2">
                        <Crown className="h-5 w-5 text-yellow-500" />
                        <span>Pro Annual</span>
                        <Badge className="bg-green-500">Best Value</Badge>
                      </CardTitle>
                      <CardDescription>Full access for one year</CardDescription>
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">$29</span>
                    <span className="text-gray-500">/year</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Less than $2.50/month</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-4">
                    {['Up to 20 programs', 'Up to 200 athletes', 'Team collaboration (up to 5)', 'PDF & Excel exports', 'Full analytics dashboard'].map((f, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <Check className="h-4 w-4 text-green-500 mr-2 shrink-0" />{f}
                      </li>
                    ))}
                  </ul>

                  {/* Payment Method Picker - Pro */}
                  <div className="space-y-3">
                    {billingConfig?.card_enabled && billingConfig?.paypal_enabled !== false && (
                      <div className="flex rounded-lg border overflow-hidden text-sm" data-testid="pro-payment-method-picker">
                        <button
                          className={`flex-1 py-2 px-3 flex items-center justify-center gap-1.5 transition-colors ${paymentMethod === 'card' ? 'bg-blue-50 text-blue-700 font-medium border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
                          onClick={() => setPaymentMethod('card')}
                        >
                          <CreditCard className="h-3.5 w-3.5" />Card
                        </button>
                        <button
                          className={`flex-1 py-2 px-3 flex items-center justify-center gap-1.5 transition-colors ${paymentMethod === 'paypal' ? 'bg-blue-50 text-blue-700 font-medium border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
                          onClick={() => setPaymentMethod('paypal')}
                        >
                          PayPal
                        </button>
                      </div>
                    )}

                    {paymentMethod === 'card' && billingConfig?.card_enabled ? (
                      <Button
                        onClick={() => handleCardCheckout('PRO_ANNUAL')}
                        disabled={cardProcessing || processing}
                        className="w-full h-11"
                        data-testid="pay-pro-card-btn"
                      >
                        {cardProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                        Pay $29.00 — Card
                      </Button>
                    ) : paypalClientId && billingConfig?.paypal_enabled !== false ? (
                      <Button
                        onClick={() => handlePayPalPayment('yearly')}
                        disabled={processing}
                        className="w-full h-11"
                        style={{ backgroundColor: '#0070ba' }}
                        data-testid="pay-pro-paypal-btn"
                      >
                        {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Pay $29.00 with PayPal
                      </Button>
                    ) : null}

                    {billingConfig?.card_mode === 'simulator' && paymentMethod === 'card' && (
                      <p className="text-xs text-amber-600 text-center font-medium">TEST MODE — no real charges</p>
                    )}
                    <p className="text-xs text-gray-400 text-center">Card details are entered on Plug'n Pay's secure page — we never see your card number. Visa and Mastercard · charged in USD.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Enterprise Plan */}
            <Card className="border-2 border-indigo-500 shadow-lg" data-testid="enterprise-plan-card">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <Key className="h-5 w-5 text-indigo-600" />
                      <span>Enterprise</span>
                      <Badge className="bg-indigo-600">API + Branding</Badge>
                    </CardTitle>
                    <CardDescription>White-label + REST API access</CardDescription>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-4xl font-bold">$99</span>
                  <span className="text-gray-500">/year</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Everything in Pro, plus:</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-4">
                  {[
                    'Unlimited programs',
                    'Up to 1,000 athletes',
                    'Team collaboration (up to 25)',
                    'White-label branding',
                    'REST API access with API keys',
                    'Custom brand colors & logo'
                  ].map((f, i) => (
                    <li key={i} className="flex items-center text-sm">
                      <Check className="h-4 w-4 text-indigo-500 mr-2 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                
                {/* Payment Method Picker - Enterprise */}
                <div className="space-y-3">
                  {billingConfig?.card_enabled && billingConfig?.paypal_enabled !== false && (
                    <div className="flex rounded-lg border overflow-hidden text-sm" data-testid="enterprise-payment-method-picker">
                      <button
                        className={`flex-1 py-2 px-3 flex items-center justify-center gap-1.5 transition-colors ${paymentMethod === 'card' ? 'bg-indigo-50 text-indigo-700 font-medium border-b-2 border-indigo-600' : 'text-gray-500 hover:bg-gray-50'}`}
                        onClick={() => setPaymentMethod('card')}
                      >
                        <CreditCard className="h-3.5 w-3.5" />Card
                      </button>
                      <button
                        className={`flex-1 py-2 px-3 flex items-center justify-center gap-1.5 transition-colors ${paymentMethod === 'paypal' ? 'bg-indigo-50 text-indigo-700 font-medium border-b-2 border-indigo-600' : 'text-gray-500 hover:bg-gray-50'}`}
                        onClick={() => setPaymentMethod('paypal')}
                      >
                        PayPal
                      </button>
                    </div>
                  )}

                  {paymentMethod === 'card' && billingConfig?.card_enabled ? (
                    <Button
                      onClick={() => handleCardCheckout('ENTERPRISE_ANNUAL')}
                      disabled={cardProcessing || processing}
                      className="w-full h-11 bg-indigo-600 hover:bg-indigo-700"
                      data-testid="pay-enterprise-card-btn"
                    >
                      {cardProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                      {isPaid ? 'Upgrade to Enterprise — $99.00' : 'Pay $99.00 — Card'}
                    </Button>
                  ) : paypalClientId && billingConfig?.paypal_enabled !== false ? (
                    <Button 
                      onClick={() => handlePayPalPayment('enterprise')}
                      disabled={processing}
                      className="w-full h-11 bg-indigo-600 hover:bg-indigo-700"
                      data-testid="pay-enterprise-paypal-btn"
                    >
                      {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {isPaid ? 'Upgrade to Enterprise — $99.00' : 'Pay $99.00 with PayPal'}
                    </Button>
                  ) : null}

                  {billingConfig?.card_mode === 'simulator' && paymentMethod === 'card' && (
                    <p className="text-xs text-amber-600 text-center font-medium">TEST MODE — no real charges</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div id="paypal-button-container" className="max-w-md mx-auto"></div>

          {/* Coupon Redemption */}
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Ticket className="h-5 w-5 text-blue-600" />
                <span>Have a Coupon Code?</span>
              </CardTitle>
              <CardDescription>Enter your coupon code to get a discount or free upgrade</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  data-testid="coupon-code-input"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value.toUpperCase());
                    if (couponStatus) setCouponStatus(null);
                  }}
                  placeholder="Enter coupon code"
                  className="uppercase font-mono tracking-wider"
                  onKeyDown={(e) => e.key === 'Enter' && handleValidateCoupon()}
                />
                <Button
                  data-testid="validate-coupon-btn"
                  onClick={handleValidateCoupon}
                  disabled={!couponCode.trim() || couponLoading}
                  variant="outline"
                >
                  {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>

              {couponStatus && (
                <div data-testid="coupon-status">
                  {couponStatus.valid ? (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-3">
                      <div className="flex items-center gap-2 text-green-800">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="font-medium">
                          {couponStatus.discount_type === 'free_upgrade'
                            ? 'Free Pro Upgrade!'
                            : couponStatus.description || `${couponStatus.discount_value}% off`}
                        </span>
                      </div>
                      {couponStatus.discount_type === 'free_upgrade' ? (
                        <div className="space-y-2">
                          <p className="text-sm text-green-700">This coupon grants a full year of Pro access at no cost.</p>
                          <Button
                            data-testid="redeem-coupon-btn"
                            onClick={handleRedeemCoupon}
                            disabled={redeemingCoupon}
                            className="w-full bg-green-600 hover:bg-green-700"
                          >
                            {redeemingCoupon ? (
                              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Activating...</>
                            ) : (
                              <><Tag className="h-4 w-4 mr-2" /> Redeem - Activate Pro Now</>
                            )}
                          </Button>
                        </div>
                      ) : couponStatus.discounted_price !== undefined ? (
                        <div className="space-y-2">
                          <p className="text-sm text-green-700">
                            <span className="line-through text-gray-400">${couponStatus.original_price}</span>{' '}
                            <span className="text-lg font-bold">${couponStatus.discounted_price}</span>/year
                          </p>
                          <p className="text-xs text-green-600">Proceed with PayPal below to pay the discounted price.</p>
                        </div>
                      ) : (
                        <Button
                          data-testid="redeem-coupon-btn"
                          onClick={handleRedeemCoupon}
                          disabled={redeemingCoupon}
                          className="w-full bg-green-600 hover:bg-green-700"
                        >
                          {redeemingCoupon ? (
                            <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Activating...</>
                          ) : (
                            <><Tag className="h-4 w-4 mr-2" /> Redeem Coupon</>
                          )}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-center gap-2 text-red-700" data-testid="coupon-error">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">{couponStatus.message}</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Section */}
          {!isPaid && (
            <Card className="max-w-md mx-auto">
              <CardHeader>
                <CardTitle>Other Payment Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  onClick={handleContactForPayment}
                  variant="outline"
                  className="w-full"
                >
                  Contact Us to Upgrade
                </Button>
                <p className="text-xs text-center text-gray-500">
                  Questions? Email us at info@attendancetracker.ws
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Enterprise Features (only for enterprise users) */}
      {isEnterprise && (
        <>
          {/* API Key Management */}
          <Card data-testid="api-keys-section">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Key className="h-5 w-5 text-indigo-600" />
                <span>API Keys</span>
              </CardTitle>
              <CardDescription>Manage API keys for external integrations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Create new key */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>Key Name</Label>
                  <Input
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="e.g. My Integration"
                    data-testid="api-key-name-input"
                  />
                </div>
                <div>
                  <Label>Scope</Label>
                  <select
                    className="h-10 px-3 border rounded-md text-sm"
                    value={newKeyScopes.includes('write') ? 'read,write' : 'read'}
                    onChange={(e) => setNewKeyScopes(e.target.value.split(','))}
                    data-testid="api-key-scope-select"
                  >
                    <option value="read">Read Only</option>
                    <option value="read,write">Read + Write</option>
                  </select>
                </div>
                <Button
                  onClick={handleCreateApiKey}
                  disabled={!newKeyName.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="create-api-key-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create
                </Button>
              </div>

              {/* Newly created key */}
              {createdKey && showCreatedKey && (
                <Alert className="border-green-300 bg-green-50">
                  <AlertDescription>
                    <p className="font-medium text-green-800 mb-1">API Key Created — Copy it now!</p>
                    <div className="flex items-center gap-2 bg-white rounded border p-2 font-mono text-xs break-all">
                      <code data-testid="created-api-key">{createdKey.key}</code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { navigator.clipboard.writeText(createdKey.key); toast({ title: 'Copied!' }); }}
                        data-testid="copy-api-key-btn"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <p className="text-xs text-green-600 mt-1">This key won't be shown again.</p>
                  </AlertDescription>
                </Alert>
              )}

              {/* Existing keys */}
              {apiKeys.length > 0 ? (
                <div className="space-y-2">
                  {apiKeys.map(k => (
                    <div key={k.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`api-key-row-${k.id}`}>
                      <div>
                        <p className="font-medium text-sm">{k.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{k.key_preview} · {k.scopes?.join(', ')} · Created {new Date(k.created_at).toLocaleDateString()}</p>
                        {k.last_used_at && <p className="text-xs text-gray-400">Last used: {new Date(k.last_used_at).toLocaleDateString()}</p>}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-500 border-red-200 hover:bg-red-50"
                        onClick={() => handleRevokeKey(k.id)}
                        data-testid={`revoke-key-${k.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Revoke
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No API keys yet. Create one to get started.</p>
              )}

              {/* API Docs snippet */}
              <div className="mt-4 p-4 bg-gray-900 rounded-lg text-green-300 font-mono text-xs overflow-x-auto">
                <p className="text-gray-400 mb-2"># Example: List your programs</p>
                <p>curl -H "Authorization: Bearer YOUR_API_KEY" \</p>
                <p className="pl-4">{process.env.REACT_APP_BACKEND_URL}/api/v1/programs</p>
              </div>
            </CardContent>
          </Card>

          {/* White-Label Branding */}
          <Card data-testid="white-label-section">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Palette className="h-5 w-5 text-indigo-600" />
                <span>White-Label Branding</span>
              </CardTitle>
              <CardDescription>Customize the look and feel of your app</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Brand Name</Label>
                  <Input
                    value={whiteLabel.brand_name}
                    onChange={(e) => setWhiteLabel({ ...whiteLabel, brand_name: e.target.value })}
                    placeholder="Your Organization Name"
                    data-testid="brand-name-input"
                  />
                </div>
                <div>
                  <Label>Logo URL</Label>
                  <Input
                    value={whiteLabel.logo_url || ''}
                    onChange={(e) => setWhiteLabel({ ...whiteLabel, logo_url: e.target.value })}
                    placeholder="https://example.com/logo.png"
                    data-testid="brand-logo-input"
                  />
                </div>
                <div>
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={whiteLabel.primary_color}
                      onChange={(e) => setWhiteLabel({ ...whiteLabel, primary_color: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                      data-testid="primary-color-picker"
                    />
                    <Input
                      value={whiteLabel.primary_color}
                      onChange={(e) => setWhiteLabel({ ...whiteLabel, primary_color: e.target.value })}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label>Accent Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={whiteLabel.accent_color}
                      onChange={(e) => setWhiteLabel({ ...whiteLabel, accent_color: e.target.value })}
                      className="w-10 h-10 rounded border cursor-pointer"
                      data-testid="accent-color-picker"
                    />
                    <Input
                      value={whiteLabel.accent_color}
                      onChange={(e) => setWhiteLabel({ ...whiteLabel, accent_color: e.target.value })}
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={whiteLabel.hide_powered_by}
                  onChange={(e) => setWhiteLabel({ ...whiteLabel, hide_powered_by: e.target.checked })}
                  className="rounded border-gray-300"
                  data-testid="hide-powered-by-checkbox"
                />
                <span className="text-sm text-gray-700">Hide "Powered by Athlete Attendance" footer</span>
              </label>
              {/* Preview */}
              {whiteLabel.brand_name && (
                <div className="p-4 border rounded-lg bg-white">
                  <p className="text-xs text-gray-400 mb-2">Preview:</p>
                  <div className="flex items-center gap-3">
                    {whiteLabel.logo_url && <img src={whiteLabel.logo_url} alt="" className="h-8 w-8 rounded object-contain" />}
                    <span className="font-bold text-lg" style={{ color: whiteLabel.primary_color }}>{whiteLabel.brand_name}</span>
                  </div>
                </div>
              )}
              <Button
                onClick={handleSaveBranding}
                disabled={savingBrand}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-branding-btn"
              >
                {savingBrand ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Branding
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {/* Plan Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Plan Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Feature</th>
                  <th className="text-center py-3 px-4">Free</th>
                  <th className="text-center py-3 px-4 bg-purple-50">Pro — $29/yr</th>
                  <th className="text-center py-3 px-4 bg-indigo-50">Enterprise — $99/yr</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Programs', free: '3', pro: '20', enterprise: 'Unlimited' },
                  { feature: 'Athletes', free: '30', pro: '200', enterprise: '1,000' },
                  { feature: 'Team Members', free: '1 (you)', pro: '5', enterprise: '25' },
                  { feature: 'Attendance History', free: '30 days', pro: 'Unlimited', enterprise: 'Unlimited' },
                  { feature: 'Team Collaboration', free: false, pro: true, enterprise: true },
                  { feature: 'CSV/PDF/Excel Exports', free: false, pro: true, enterprise: true },
                  { feature: 'White-Label Branding', free: false, pro: false, enterprise: true },
                  { feature: 'REST API Access', free: false, pro: false, enterprise: true },
                  { feature: 'Custom Brand Colors', free: false, pro: false, enterprise: true }
                ].map((row, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="py-3 px-4">{row.feature}</td>
                    <td className="text-center py-3 px-4">
                      {typeof row.free === 'boolean' ? (
                        row.free ? <Check className="h-5 w-5 text-green-500 mx-auto" /> : <X className="h-5 w-5 text-gray-300 mx-auto" />
                      ) : row.free}
                    </td>
                    <td className="text-center py-3 px-4 bg-purple-50">
                      {typeof row.pro === 'boolean' ? (
                        row.pro ? <Check className="h-5 w-5 text-green-500 mx-auto" /> : <X className="h-5 w-5 text-gray-300 mx-auto" />
                      ) : row.pro}
                    </td>
                    <td className="text-center py-3 px-4 bg-indigo-50">
                      {typeof row.enterprise === 'boolean' ? (
                        row.enterprise ? <Check className="h-5 w-5 text-indigo-600 mx-auto" /> : <X className="h-5 w-5 text-gray-300 mx-auto" />
                      ) : row.enterprise}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Subscription;
